import { groupBy, isNil, last, sortBy, uniqBy } from "lodash-es";
import type { PillVariant } from "../components/pill/Pill";
import type { TranslationKey } from "../state/i18n";
import type { Task, TaskFulfilment, TaskStatus, TaskUser } from "../types/Task";
import { keyFromSelector } from "i18next";

export type FulfilmentStatus = { label: TranslationKey; variant: PillVariant };

export const STATUS_MAP: Record<TaskStatus, FulfilmentStatus> = {
  IN_PROGRESS: { label: keyFromSelector(($) => $.TASKS_STATUS_PENDING), variant: "neutral" },
  COMPLETED: { label: keyFromSelector(($) => $.TASKS_STATUS_COMPLETED), variant: "success" },
  REVOKED: { label: keyFromSelector(($) => $.TASKS_STATUS_REVOKED), variant: "warning" },
  DECLINED: { label: keyFromSelector(($) => $.TASKS_STATUS_DECLINED), variant: "danger" },
};

export const getFulfilmentStatus = (taskFulfilment: TaskFulfilment | undefined): FulfilmentStatus | undefined => {
  if (isNil(taskFulfilment)) {
    return undefined;
  }

  if (isNil(taskFulfilment.registrationId)) {
    return { label: keyFromSelector(($) => $.TASKS_STATUS_DECLINED), variant: "danger" };
  }

  return { label: keyFromSelector(($) => $.TASKS_STATUS_COMPLETED), variant: "success" };
};

// The current user of a request of the task, with their fulfilment
export type TaskRecipient = { user: TaskUser; fulfilment: TaskFulfilment | undefined };

// A request of the task: its current user and its draft when known
type TaskRequestSlot = { user: TaskUser; draftSubmissionId: string | undefined };

// Lists the current user of every request of the task. Users a request was reassigned from are left out.
export const getTaskRecipients = (task: Task): TaskRecipient[] =>
  assignFulfilments(getRequestSlots(task), task.fulfilments);

// A request keeps its draft when it's reassigned, so the last transfer of a draft tells which request of its user has
// that draft. A transfer without a draft replaced its request, so its request has no known draft.
const getRequestSlots = (task: Task): TaskRequestSlot[] => {
  const unclaimedHistories = Object.values(
    groupBy(
      sortBy(
        task.transfers.filter(({ draftSubmissionId }) => !isNil(draftSubmissionId)),
        ({ date }) => date.getTime(),
      ),
      ({ draftSubmissionId }) => draftSubmissionId,
    ),
  );

  return task.users.map((user) => {
    const index = unclaimedHistories.findIndex((transfers) => last(transfers)?.to.userId.equals(user.userId));
    if (index < 0) {
      return { user, draftSubmissionId: undefined };
    }
    const [transfers] = unclaimedHistories.splice(index, 1);
    return { user, draftSubmissionId: transfers[0].draftSubmissionId };
  });
};

// Fulfilments are matched by draft first. The rest (older fulfilments and declines without a draft) go to the user's
// requests in order, without giving a request with a known draft the fulfilment of another draft.
const assignFulfilments = (slots: TaskRequestSlot[], fulfilments: TaskFulfilment[]): TaskRecipient[] => {
  const unassigned = [...fulfilments];
  const take = (predicate: (fulfilment: TaskFulfilment) => boolean): TaskFulfilment | undefined => {
    const index = unassigned.findIndex(predicate);
    return index < 0 ? undefined : unassigned.splice(index, 1)[0];
  };

  const matchedByDraft = slots.map(({ draftSubmissionId }) =>
    isNil(draftSubmissionId) ? undefined : take((fulfilment) => fulfilment.draftSubmissionId === draftSubmissionId),
  );

  return slots.map((slot, index) => ({
    user: slot.user,
    fulfilment:
      matchedByDraft[index] ??
      take(
        (fulfilment) =>
          fulfilment.userId.equals(slot.user.userId) &&
          (isNil(slot.draftSubmissionId) || isNil(fulfilment.draftSubmissionId)),
      ),
  }));
};

export const getTaskRecipientStatus = (recipient: TaskRecipient, taskStatus: TaskStatus): FulfilmentStatus =>
  getFulfilmentStatus(recipient.fulfilment) ?? STATUS_MAP[taskStatus];

// Users who already completed or declined their request can't hand it over anymore
export const getReassignableUsers = (task: Task): TaskUser[] =>
  uniqBy(
    getTaskRecipients(task)
      .filter(({ fulfilment }) => isNil(fulfilment))
      .map(({ user }) => user),
    ({ userId }) => userId.toString(),
  );
